/**
 * ABWIN Scratch Popup — Main Controller
 * ---------------------------------------------------------------
 * Owns: DOM construction, state machine, trigger scheduling, focus
 * trap / keyboard access, and wiring between scratchEngine, email,
 * discount, analytics, and storage.
 *
 * State machine: scratch -> revealed -> email -> success
 */
(function (global) {
  "use strict";

  const cfg = global.ABWIN_POPUP_CONFIG;
  const { EVENTS, track } = global.AbwinAnalytics;
  const Storage = global.AbwinStorage;

  const FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  class AbwinScratchPopup {
    constructor() {
      this.state = "scratch";
      this.root = null;
      this.scratchEngineInstance = null;
      this._keydownHandler = this._keydownHandler.bind(this);
      this._lastFocusedBeforeOpen = null;
    }

    scheduleTrigger() {
      const eligibility = Storage.isEligibleToShow();
      if (!eligibility.allowed) {
        if (cfg.analytics.devLogging) {
          console.log(`[abwin-popup] suppressed: ${eligibility.reason}`);
        }
        return;
      }

      const { trigger } = cfg;
      if (trigger.type === "scroll") {
        this._scheduleScrollTrigger(trigger.scrollPercent);
      } else if (trigger.type === "exit-intent" && trigger.enableExitIntentOnDesktop) {
        this._scheduleExitIntentTrigger();
      } else {
        setTimeout(() => this.open(), trigger.delayMs);
      }
    }

    _scheduleScrollTrigger(percent) {
      const onScroll = () => {
        const scrolled =
          (global.scrollY /
            (document.documentElement.scrollHeight - global.innerHeight)) *
          100;
        if (scrolled >= percent) {
          global.removeEventListener("scroll", onScroll);
          this.open();
        }
      };
      global.addEventListener("scroll", onScroll, { passive: true });
    }

    _scheduleExitIntentTrigger() {
      const onMouseOut = (e) => {
        if (e.clientY <= 0) {
          document.removeEventListener("mouseout", onMouseOut);
          this.open();
        }
      };
      document.addEventListener("mouseout", onMouseOut);
    }

    open() {
      if (this.root) return;
      this._lastFocusedBeforeOpen = document.activeElement;
      this.state = "scratch";
      this._render();
      document.addEventListener("keydown", this._keydownHandler);
      Storage.markShownThisSession();
      track(EVENTS.POPUP_VIEWED, {});
      requestAnimationFrame(() => {
        this.root.classList.add("is-open");
        this._focusFirstElement();
      });
    }

    close(reason) {
      if (!this.root) return;
      track(EVENTS.POPUP_CLOSED, { reason: reason || "manual", state: this.state });
      Storage.markClosed();
      document.removeEventListener("keydown", this._keydownHandler);
      if (this.scratchEngineInstance) {
        this.scratchEngineInstance.destroy();
        this.scratchEngineInstance = null;
      }
      this.root.classList.remove("is-open");
      const node = this.root;
      this.root = null;
      setTimeout(() => node.remove(), 220);
      if (this._lastFocusedBeforeOpen && this._lastFocusedBeforeOpen.focus) {
        this._lastFocusedBeforeOpen.focus();
      }
    }

    _keydownHandler(e) {
      if (e.key === "Escape") {
        this.close("escape_key");
        return;
      }
      if (e.key === "Tab") {
        this._trapFocus(e);
      }
    }

    _trapFocus(e) {
      const focusables = Array.from(
        this.root.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    _focusFirstElement() {
      const closeBtn = this.root.querySelector(".abwin-scratch-close");
      if (closeBtn) closeBtn.focus();
    }

    _render() {
      const wrapper = document.createElement("div");
      wrapper.className = "abwin-scratch-popup";
      wrapper.setAttribute("role", "dialog");
      wrapper.setAttribute("aria-modal", "true");
      wrapper.setAttribute("aria-label", "ABWIN exclusive offer");
      wrapper.innerHTML = this._template();
      document.body.appendChild(wrapper);
      this.root = wrapper;

      wrapper
        .querySelector(".abwin-scratch-backdrop")
        .addEventListener("click", () => this.close("backdrop_click"));
      wrapper
        .querySelectorAll(".abwin-scratch-close")
        .forEach((btn) => btn.addEventListener("click", () => this.close("close_button")));

      if (cfg.layout.showProductRail) {
        this._renderProductRail();
      }
      this._initScratchScreen();
    }

    _template() {
      const rails = cfg.layout.showProductRail
        ? `
          <div class="abwin-scratch-rail abwin-scratch-rail--left" data-abwin-rail></div>
          <div class="abwin-scratch-content" data-abwin-content></div>
          <div class="abwin-scratch-rail abwin-scratch-rail--right" data-abwin-rail></div>
        `
        : `<div class="abwin-scratch-content" data-abwin-content></div>`;

      return `
        <div class="abwin-scratch-backdrop"></div>
        <div class="abwin-scratch-card-outer${cfg.layout.showProductRail ? " has-rail" : ""}">
          <button type="button" class="abwin-scratch-close" aria-label="${cfg.copy.close.ariaLabel}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
          ${rails}
        </div>
      `;
    }

    _renderProductRail() {
      const showImages = global.innerWidth >= cfg.mobile.breakpointPx;
      const rails = this.root.querySelectorAll("[data-abwin-rail]");
      if (!showImages) {
        rails.forEach((r) => r.remove());
        return;
      }
      const [left, right] = rails;
      const products = cfg.products;
      const half = Math.ceil(products.length / 2);
      this._fillRail(left, products.slice(0, half));
      this._fillRail(right, products.slice(half));
    }

    _fillRail(railEl, products) {
      railEl.innerHTML = products
        .map(
          (p) => `
          <div class="abwin-scratch-product">
            <img src="${p.src}" alt="${p.name}" loading="lazy" width="140" height="94" />
          </div>`
        )
        .join("");
    }

    _initScratchScreen() {
      const content = this.root.querySelector("[data-abwin-content]");
      const c = cfg.copy.scratch;
      content.innerHTML = `
        <div class="abwin-scratch-screen" data-state="scratch">
          <img class="abwin-scratch-logo" src="${cfg.brand.logo}" alt="${cfg.brand.logoAlt}" />
          <p class="abwin-scratch-eyebrow">${c.eyebrow}</p>
          <h2 class="abwin-scratch-headline">${c.headline}</h2>
          <p class="abwin-scratch-subheadline">${c.subheadline}</p>
          <div class="abwin-scratch-canvas-wrap">
            <canvas class="abwin-scratch-canvas" aria-hidden="true"></canvas>
            <div class="abwin-scratch-reward-preview" data-abwin-reward-preview></div>
          </div>
          <p class="abwin-scratch-footnote">${c.footnote}</p>
          <button type="button" class="abwin-scratch-link-btn" data-abwin-reveal-fallback>
            ${c.accessibleRevealButton}
          </button>
        </div>
      `;

      const discount = global.AbwinDiscount.getDiscount();
      content.querySelector("[data-abwin-reward-preview]").innerHTML = `
        <span class="abwin-scratch-reward-percent">${discount.label}</span>
      `;

      const canvas = content.querySelector(".abwin-scratch-canvas");
      const engine = new global.AbwinScratchEngine(canvas, {
        threshold: cfg.scratch.threshold,
        brushRadius: this._responsiveBrushRadius(),
        sampleIntervalMs: cfg.scratch.sampleIntervalMs,
        foilColors: [
          cfg.brand.colors.purple,
          cfg.brand.colors.purpleDark,
          "#2a0c40",
        ],
        foilLabel: cfg.copy.scratch.scratchLabel,
        onStart: () => track(EVENTS.SCRATCH_STARTED, {}),
        onProgress: () => {},
        onThresholdReached: () => {
          track(EVENTS.SCRATCH_COMPLETED, {});
          this._goToRevealed();
        },
      });
      engine.init();
      this.scratchEngineInstance = engine;

      content
        .querySelector("[data-abwin-reveal-fallback]")
        .addEventListener("click", () => engine.revealAll());
    }

    _responsiveBrushRadius() {
      return global.innerWidth < cfg.mobile.breakpointPx
        ? Math.round(cfg.scratch.brushRadius * 0.8)
        : cfg.scratch.brushRadius;
    }

    _goToRevealed() {
      this.state = "revealed";
      const discount = global.AbwinDiscount.getDiscount();
      const c = cfg.copy.reveal;
      const content = this.root.querySelector("[data-abwin-content]");

      content.innerHTML = `
        <div class="abwin-scratch-screen abwin-scratch-screen--celebrate" data-state="revealed">
          <p class="abwin-scratch-eyebrow">${c.headline}</p>
          <div class="abwin-scratch-big-number">${discount.label}</div>
          <p class="abwin-scratch-subheadline">${c.microcopy}</p>
          <button type="button" class="abwin-scratch-cta" data-abwin-claim>
            ${c.claimButton}
          </button>
          <p class="abwin-scratch-microcopy">${c.secondary}</p>
        </div>
      `;

      track(EVENTS.REWARD_REVEALED, { discountPercent: discount.percent });

      content.querySelector("[data-abwin-claim]").addEventListener("click", () => {
        track(EVENTS.CLAIM_CLICKED, { discountPercent: discount.percent });
        this._goToEmail();
      });
    }

    _goToEmail() {
      this.state = "email";
      const c = cfg.copy.email;
      const content = this.root.querySelector("[data-abwin-content]");

      content.innerHTML = `
        <div class="abwin-scratch-screen" data-state="email">
          <h2 class="abwin-scratch-headline abwin-scratch-headline--sm">${c.headline}</h2>
          <p class="abwin-scratch-subheadline">${c.subheadline}</p>
          <form class="abwin-scratch-form" data-abwin-email-form novalidate>
            <label class="abwin-scratch-visually-hidden" for="abwin-scratch-email-input">
              ${c.placeholder}
            </label>
            <input
              id="abwin-scratch-email-input"
              class="abwin-scratch-input"
              type="email"
              inputmode="email"
              autocomplete="email"
              placeholder="${c.placeholder}"
              data-abwin-email-input
              required
            />
            <p class="abwin-scratch-error" data-abwin-email-error role="alert" hidden></p>
            <button type="submit" class="abwin-scratch-cta" data-abwin-submit>
              ${c.submitButton}
            </button>
          </form>
          <p class="abwin-scratch-microcopy">${c.reassurance}</p>
          <button type="button" class="abwin-scratch-link-btn" data-abwin-back>${c.backLink}</button>
        </div>
      `;

      track(EVENTS.EMAIL_FORM_VIEWED, {});

      const form = content.querySelector("[data-abwin-email-form]");
      const input = content.querySelector("[data-abwin-email-input]");
      const errorEl = content.querySelector("[data-abwin-email-error]");
      const submitBtn = content.querySelector("[data-abwin-submit]");

      content
        .querySelector("[data-abwin-back]")
        .addEventListener("click", () => this._goToRevealed());

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        this._handleEmailSubmit(input.value, { errorEl, submitBtn, input });
      });

      input.focus();
    }

    _handleEmailSubmit(rawEmail, { errorEl, submitBtn, input }) {
      const c = cfg.copy.email;

      if (!global.AbwinEmail.isValidEmail(rawEmail)) {
        errorEl.textContent = c.invalidEmail;
        errorEl.hidden = false;
        input.setAttribute("aria-invalid", "true");
        input.focus();
        return;
      }

      errorEl.hidden = true;
      input.removeAttribute("aria-invalid");
      submitBtn.disabled = true;
      submitBtn.classList.add("is-loading");

      global.AbwinEmail.submit(rawEmail)
        .then(() => {
          track(EVENTS.EMAIL_SUBMITTED, { hasEmail: true });
          const discount = global.AbwinDiscount.getDiscount();
          track(EVENTS.DISCOUNT_REVEALED, { discountPercent: discount.percent });
          Storage.markClaimed(discount.code, rawEmail);
          this._goToSuccess(rawEmail);
        })
        .catch((err) => {
          track(EVENTS.EMAIL_SUBMISSION_FAILED, {
            errorMessage: err && err.message,
          });
          errorEl.textContent = c.genericError;
          errorEl.hidden = false;
          submitBtn.disabled = false;
          submitBtn.classList.remove("is-loading");
        });
    }

    _goToSuccess() {
      this.state = "success";
      const discount = global.AbwinDiscount.getDiscount();
      const c = cfg.copy.success;
      const content = this.root.querySelector("[data-abwin-content]");

      content.innerHTML = `
        <div class="abwin-scratch-screen abwin-scratch-screen--celebrate" data-state="success">
          <h2 class="abwin-scratch-headline abwin-scratch-headline--sm">${c.headline}</h2>
          <div class="abwin-scratch-big-number abwin-scratch-big-number--sm">${discount.label}</div>
          <p class="abwin-scratch-subheadline">${c.microcopy}</p>

          <div class="abwin-scratch-code-row">
            <code class="abwin-scratch-code" data-abwin-code>${discount.code}</code>
            <button type="button" class="abwin-scratch-copy-btn" data-abwin-copy>
              ${c.copyButton}
            </button>
          </div>
          <p class="abwin-scratch-manual-hint">${c.manualEntryLabel}</p>

          <a class="abwin-scratch-cta abwin-scratch-cta--link" href="${cfg.shopUrl}" data-abwin-shop>
            ${c.shopButton}
          </a>
        </div>
      `;

      const copyBtn = content.querySelector("[data-abwin-copy]");
      copyBtn.addEventListener("click", () => this._handleCopy(copyBtn, discount.code));

      content.querySelector("[data-abwin-shop]").addEventListener("click", () => {
        track(EVENTS.SHOP_NOW_CLICKED, { discountPercent: discount.percent });
      });
    }

    _handleCopy(button, code) {
      const c = cfg.copy.success;
      const done = () => {
        track(EVENTS.DISCOUNT_COPIED, {});
        button.textContent = c.copiedButton;
        button.classList.add("is-copied");
        setTimeout(() => {
          button.textContent = c.copyButton;
          button.classList.remove("is-copied");
        }, 2200);
      };

      if (global.navigator.clipboard && global.navigator.clipboard.writeText) {
        global.navigator.clipboard.writeText(code).then(done).catch(() => this._fallbackCopy(code, done));
      } else {
        this._fallbackCopy(code, done);
      }
    }

    _fallbackCopy(code, done) {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        /* ignore */
      }
      document.body.removeChild(textarea);
      done();
    }
  }

  global.AbwinScratchPopup = AbwinScratchPopup;
})(window);
